import { useState, useCallback } from "react";
import type { MouseEvent, ReactElement } from "react";
import { PopupMenu } from "./PopupMenu";
import type { PopupMenuSection } from "./PopupMenu";

export interface UsePopupMenuResult {
  /** The rendered <PopupMenu /> — insert it anywhere in the JSX tree. */
  menu: ReactElement;
  /** Attach to the onClick of any element to open the menu anchored to it. */
  setPopupMenuAnchor: (e: MouseEvent<HTMLElement>) => void;
}

/**
 * Manages anchor, open state, close logic, and renders the PopupMenu component.
 * Returns the menu element to insert and setPopupMenuAnchor for the trigger element.
 */
export function usePopupMenu(sections: PopupMenuSection[]): UsePopupMenuResult {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const onClose = useCallback(() => setAnchorEl(null), []);

  const setPopupMenuAnchor = useCallback((e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const menu = (
    <PopupMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose} sections={sections} />
  );

  return { menu, setPopupMenuAnchor };
}
